/**
 * Shared constants for the STW Platform
 * Use ALL_CAPS naming convention for constants
 */

/**
 * Current engine version stamped on all newly created items and rooms.
 *
 * Version history:
 *   "0"     - Legacy (staging). World-space coordinates, baked scale in walls,
 *             offset = display model Y shift, pillars/floor/walls not Room children.
 *   "1.0.0" - Object hierarchy. Room-local coordinates, Room.scale applied via
 *             Object3D, offset = floor height, all surfaces are Room children.
 *   "1.1.0" - Room-shape modernization ahead of the scene/scene_object split. RoomObject.scale
 *             normalized from a uniform number to `{x,y,z}`; each `collisions[]`
 *             entry tagged with `asset_key` (mirrors its existing `type`).
 */
export const CURRENT_ENGINE_VERSION = '1.1.0';

/**
 * Regex pattern for validating model type formats
 */
export const MODEL_TYPE_PATTERN = '^(glb|ply|ksplat)$';

/**
 * Filenames (not full URLs) of default branding assets served from the
 * frontend's own public/ directory. Each environment (local/staging/prod)
 * deploys that directory to its own domain, so a hardcoded absolute URL
 * here would be wrong for every environment but one — callers should build
 * the absolute URL from their own environment's base URL instead:
 * `CITY_URL` on the backend (see src/utils/bucket.ts's getDefaultThumbnailUrl
 * / getDefaultAvatarUrl), `window.location.origin` on the frontend.
 */
export const DEFAULT_THUMBNAIL_FILENAME = 'DEFAULT_THUMBNAIL.jpg';
export const DEFAULT_AVATAR_FILENAME = 'CITYU_WHITE_BG.png';

/**
 * Fallback avatar image shown in comment UIs when a user has no avatar.
 * Root-relative so it resolves against whatever domain the frontend is
 * served from, same asset as DEFAULT_AVATAR_FILENAME above.
 */
export const STW_LOGO_WHITE_BG_URL = '/CITYU_WHITE_BG.png';

/**
 * Default model URL used for sample items
 */
export const DEFAULT_MODEL_URL =
  'https://static.scan-the-world.com/assets/DEFAULT_MODEL.glb';

export const LARGE_HKSTP_LOGO_URL =
  'https://storage.googleapis.com/static.scan-the-world.com/assets/large_HKSTP_logo.png';

/**
 * External service URLs - used in multiple backend locations
 */
export const KIRI_API_BASE_URL = 'https://api.kiriengine.app/api/v1';
export const KIRI_VIDEO_UPLOAD_URL = `${KIRI_API_BASE_URL}/open/3dgs/video`;
export const KIRI_IMAGE_UPLOAD_URL = `${KIRI_API_BASE_URL}/open/3dgs/image`;
export const MESHY_API_BASE_URL = 'https://api.meshy.ai/openapi/v1';
export const MESHY_IMAGE_TO_3D_URL = `${MESHY_API_BASE_URL}/image-to-3d`;

/**
 * Development environment defaults
 */
export const DEFAULT_API_URL = 'http://localhost:8080/v1';
export const FIREBASE_AUTH_EMULATOR_HOST = 'localhost:9098';
export const FIREBASE_STORAGE_EMULATOR_HOST = 'localhost:9198';

/**
 * Avatar upload constraints
 */
export const AVATAR_MAX_SIZE = 2 * 1024 * 1024; // 2MB
export const AVATAR_ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
];

/**
 * Firebase Storage infrastructure constants
 */
export const UNIFIED_BUCKET_NAME = 'bucket.cityujcc.scan-the-world.com';
export const TEMP_BUCKET_NAME = 'cityujcc-temp-bucket';

/**
 * View tracking types for item analytics
 */
export enum VIEW_TYPE {
  ITEM_PAGE = 'item_page',
  EMBED = 'embed',
  VIEWER = 'viewer',
}

/**
 * View tracking types for scene analytics
 */
export enum SCENE_VIEW_TYPE {
  SCENE_PAGE = 'scene_page',
  EMBED = 'embed',
}

/**
 * Feedback source platforms
 */
export const FEEDBACK_SOURCE = {
  STW_PLATFORM: 'stw_platform',
} as const;

export type FeedbackSource =
  (typeof FEEDBACK_SOURCE)[keyof typeof FEEDBACK_SOURCE];

/**
 * Feedback types/reasons
 */
export const FEEDBACK_TYPE = {
  UNINSTALL: 'uninstall',
  UNSUBSCRIBE: 'unsubscribe',
  CHURN: 'churn',
  FUTURE_REQUEST: 'future_request',
  GENERAL: 'general',
} as const;

export type FeedbackType = (typeof FEEDBACK_TYPE)[keyof typeof FEEDBACK_TYPE];
